<template>
  <div class="visitor-check-in-settings-container">
    <button
      type="button"
      class="visitor-check-in-settings-icon-button"
      :aria-label="$t('common.open_capture_settings')"
      :aria-expanded="showSettings ? 'true' : 'false'"
      @click="showSettings = !showSettings"
    >
      <i aria-hidden="true" class="ri-settings-3-fill"></i>
    </button>
    <ul
      v-if="showSettings"
      role="radiogroup"
      :aria-label="$t('common.capture_via')"
    >
      <div class="px-2 py-1 d-flex justify-content-between align-items-center">
        <div class="capture-label">{{ $t("common.capture_via") }}</div>
        <button
          type="button"
          class="visitor-check-in-settings-close-button"
          :aria-label="$t('common.close_capture_settings')"
          @click="showSettings = !showSettings"
        >
          <i class="ri-close-line"></i>
        </button>
      </div>
      <li v-for="option in menuOptions" :key="option.key">
        <button
          type="button"
          class="visitor-check-in-settings-option-button"
          role="radio"
          :aria-checked="String(state.selectedCamera === option.key)"
          @click="onDeviceSelect(option.key)"
        >
          <i :class="option.iconClass"></i>
          {{ option.name }}
          <span v-if="state.selectedCamera === option.key" class="ms-2">
            ({{ $t("common.selected") }})
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import visitorConstants from "@/constants/visitorConstants"
import { onMounted, reactive, ref } from "vue"
import { useI18n } from "vue-i18n"

export default {
  name: "VisitorCaptureViaMenu",
  emits: ["notify", "device-select"],
  setup(_props, { emit }) {
    const { t } = useI18n()
    const showSettings = ref(false)
    const state = reactive({
      selectedCamera: null
    })

    const menuOptions = [
      {
        key: visitorConstants.VISITOR_CAMERA_TYPES.ANDROID,
        name: t("common.android"),
        iconClass: "ri-device-line me-2",
        show: false
      },
      {
        key: visitorConstants.VISITOR_CAMERA_TYPES.IOS,
        name: t("common.app"),
        iconClass: "ri-device-line me-2",
        show: true
      },
      {
        key: visitorConstants.VISITOR_CAMERA_TYPES.COMPUTER,
        name: t("common.computer"),
        iconClass: "ri-computer-line me-2",
        show: true
      }
    ].filter((option) => option.show)

    const safeSetDefaultCamera = (cameraType) => {
      try {
        if (typeof localStorage !== "undefined") {
          localStorage.setItem(
            visitorConstants.VISITOR_LOCAL_STORAGE_KEYS.DEFAULT_CAMERA,
            cameraType
          )
        }
      } catch {
        /* storage may be unavailable (private mode / restricted settings) */
      }
    }

    const safeGetDefaultCamera = () => {
      try {
        if (typeof localStorage !== "undefined") {
          return localStorage.getItem(
            visitorConstants.VISITOR_LOCAL_STORAGE_KEYS.DEFAULT_CAMERA
          )
        }
      } catch {
        /* storage may be unavailable (private mode / restricted settings) */
      }

      return null
    }

    const onDeviceSelect = async (name, skipNotify = false) => {
      const map = {
        android: visitorConstants.VISITOR_CAMERA_TYPES.ANDROID,
        ios: visitorConstants.VISITOR_CAMERA_TYPES.IOS,
        computer: visitorConstants.VISITOR_CAMERA_TYPES.COMPUTER
      }
      const selectedCamera = map[name]
      if (!selectedCamera) return

      safeSetDefaultCamera(selectedCamera)
      state.selectedCamera = selectedCamera
      showSettings.value = false

      if (
        !skipNotify &&
        [
          visitorConstants.VISITOR_CAMERA_TYPES.IOS,
          visitorConstants.VISITOR_CAMERA_TYPES.ANDROID
        ].includes(selectedCamera)
      ) {
        emit("notify", name)
      }

      emit("device-select", name)
    }

    onMounted(() => {
      const defaultCamera = safeGetDefaultCamera()
      if (defaultCamera) {
        onDeviceSelect(defaultCamera, true)
      } else {
        onDeviceSelect(visitorConstants.VISITOR_CAMERA_TYPES.COMPUTER, true)
      }
    })

    return {
      menuOptions,
      state,
      showSettings,
      onDeviceSelect
    }
  }
}
</script>

<style scoped></style>
